import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShowToast } from '@/hooks';
import { http } from '@/lib/ats-https';
import {
  getToastMessageForFetchError,
  SharedCalendarFetchError,
  type SharedCalendarListPollResponse,
  type SharedCalendarPollResults,
  type SharedCalendarTriggerFetchResponse,
  type UseGetSharedCalendarOptions,
} from './useGetSharedCalendar.interface';

const SHARED_CALENDAR_POLL_MAX_ATTEMPTS = 15;
const SHARED_CALENDAR_POLL_INTERVAL_MS = 3000;
const queryRoot = ['get-shared-calendars'] as const;

function parseListResponse(raw: SharedCalendarListPollResponse) {
  const listErrors = raw?.errors ?? [];
  if (listErrors.length > 0) {
    throw new SharedCalendarFetchError('api_errors', undefined, listErrors);
  }
  return raw;
}

function resultsFromSuccess(
  data: SharedCalendarListPollResponse | undefined
): SharedCalendarPollResults | null {
  if (data?.data?.status !== 'SUCCESS') return null;
  const results = data.data.results;
  if (
    results &&
    typeof results === 'object' &&
    Array.isArray(results.calendars)
  ) {
    return results;
  }
  return { calendars: [] };
}

/**
 * Same flow as {@link useSharedCalendarPolling}: trigger POST then poll GET with
 * `refetchInterval` while status is `PENDING` (TanStack Query–native polling).
 */
export const useGetSharedCalendar = (
  options: UseGetSharedCalendarOptions = {}
) => {
  const toast = useShowToast();
  const queryClient = useQueryClient();
  const listType = options.listType ?? 'saved';

  const [isTriggered, setIsTriggered] = useState(false);
  const [isMaxRetriesExceeded, setIsMaxRetriesExceeded] = useState(false);

  const toastFlags = useRef({
    maxRetriesToastShown: false,
    failureToastShown: false,
    triggerErrorToastShown: false,
    pollErrorToastShown: false,
  });

  const pollCount = useRef(0);

  const resetStates = () => {
    setIsMaxRetriesExceeded(false);
    pollCount.current = 0;
    toastFlags.current.maxRetriesToastShown = false;
    toastFlags.current.failureToastShown = false;
    toastFlags.current.triggerErrorToastShown = false;
    toastFlags.current.pollErrorToastShown = false;
  };

  const {
    data: uuidData,
    isPending: isUuidLoading,
    isFetching: isUuidFetching,
    refetch: refetchUuid,
    error: triggerError,
    isError: isTriggerError,
  } = useQuery({
    queryKey: [...queryRoot, 'trigger', listType],
    queryFn: async () => {
      resetStates();
      const res = (await http.post(
        '/shared_calendars/trigger_fetch'
      )) as SharedCalendarTriggerFetchResponse;

      const triggerErrors = res?.errors ?? [];
      if (triggerErrors.length > 0) {
        throw new SharedCalendarFetchError(
          'api_errors',
          undefined,
          triggerErrors
        );
      }

      const fetchUuid = res?.data?.fetch_uuid;
      if (!fetchUuid || typeof fetchUuid !== 'string') {
        throw new SharedCalendarFetchError('missing_fetch_uuid');
      }

      return res;
    },
    enabled: isTriggered,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const fetchUuid = uuidData?.data?.fetch_uuid;

  const {
    data: pollResponse,
    isPending: isPollLoading,
    isFetching: isPollFetching,
    error: pollError,
    isError: isPollError,
  } = useQuery({
    queryKey: [...queryRoot, 'poll', listType, fetchUuid],
    queryFn: async () => {
      if (!fetchUuid) {
        throw new Error('No fetch UUID available');
      }
      const raw = (await http.get(
        `/shared_calendars/list?fetch_uuid=${encodeURIComponent(
          fetchUuid
        )}&type=${encodeURIComponent(listType)}`
      )) as SharedCalendarListPollResponse;
      return parseListResponse(raw);
    },
    enabled: isTriggered && !!fetchUuid && !isUuidFetching,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchInterval: (queryState) => {
      const apiResponse = queryState.state.data as
        | SharedCalendarListPollResponse
        | undefined;
      const status = apiResponse?.data?.status;

      if (status === 'FAILURE') {
        if (!toastFlags.current.failureToastShown) {
          toast({
            title: getToastMessageForFetchError(
              new SharedCalendarFetchError('failure_status')
            ),
            status: 'error',
          });
          toastFlags.current.failureToastShown = true;
        }
        return false;
      }

      if (status === 'SUCCESS') {
        return false;
      }

      if (status === 'PENDING') {
        pollCount.current += 1;
        if (pollCount.current >= SHARED_CALENDAR_POLL_MAX_ATTEMPTS) {
          if (!toastFlags.current.maxRetriesToastShown) {
            toast({
              title: getToastMessageForFetchError(
                new SharedCalendarFetchError('max_attempts')
              ),
              status: 'error',
            });
            toastFlags.current.maxRetriesToastShown = true;
          }
          setIsMaxRetriesExceeded(true);
          return false;
        }
        return SHARED_CALENDAR_POLL_INTERVAL_MS;
      }

      return false;
    },
  });

  useEffect(() => {
    if (
      isTriggerError &&
      triggerError &&
      !toastFlags.current.triggerErrorToastShown
    ) {
      toastFlags.current.triggerErrorToastShown = true;
      toast({
        title: getToastMessageForFetchError(triggerError),
        status: 'error',
      });
    }
  }, [isTriggerError, triggerError, toast]);

  useEffect(() => {
    if (isPollError && pollError && !toastFlags.current.pollErrorToastShown) {
      toastFlags.current.pollErrorToastShown = true;
      toast({
        title: getToastMessageForFetchError(pollError),
        status: 'error',
      });
    }
  }, [isPollError, pollError, toast]);

  const results = useMemo(
    () => resultsFromSuccess(pollResponse),
    [pollResponse]
  );

  const calendars =
    results?.calendars?.map((calendar) => ({
      label: calendar.name,
      value: calendar.provider_calendar_id,
      calendarId: calendar.provider_calendar_id,
      userId: calendar.user_id,
      isPrimary: calendar.is_primary,
      isDefault: calendar.is_default,
    })) ?? [];

  const isLoading = useMemo(() => {
    if (!isTriggered) return false;
    const awaitingPending =
      pollResponse?.data?.status === 'PENDING' && !isMaxRetriesExceeded;
    return isUuidLoading || isPollLoading || isPollFetching || awaitingPending;
  }, [
    isTriggered,
    isUuidLoading,
    isPollLoading,
    isPollFetching,
    pollResponse?.data?.status,
    isMaxRetriesExceeded,
  ]);

  const uiError = useMemo(() => {
    const err = pollError ?? triggerError;
    if (err instanceof SharedCalendarFetchError) {
      return err.apiErrors.length > 0
        ? err.apiErrors
        : [getToastMessageForFetchError(err)];
    }
    if (err instanceof Error) {
      return [err.message];
    }
    return [];
  }, [pollError, triggerError]);

  const fetchSharedCalendars = useCallback(() => {
    setIsTriggered(true);
    return refetchUuid();
  }, [refetchUuid]);

  const reset = useCallback(() => {
    setIsTriggered(false);
    resetStates();
    queryClient.removeQueries({ queryKey: [...queryRoot] });
  }, [queryClient]);

  const isSuccess = pollResponse?.data?.status === 'SUCCESS';
  const isFailureStatus = pollResponse?.data?.status === 'FAILURE';
  const isError =
    isTriggerError || isPollError || isMaxRetriesExceeded || isFailureStatus;

  return {
    results,
    calendars,
    fetchSharedCalendars,
    uiError,
    isLoading,
    isError,
    error: pollError ?? triggerError,
    isSuccess,
    reset,
    isMaxRetriesExceeded,
    fetchUuid,
    queryKey: [...queryRoot, 'poll', listType, fetchUuid],
  };
};
